<template>
  <div id="farmVizSettings" class="mb-5">
    <!-- Block Opacity -->
    <p class="mb-0"><strong>Block Opacity:</strong> {{ farm.vizSettings.colorOpacity }}</p>
    <b-form-input
      number
      v-model="farm.vizSettings.colorOpacity"
      type="range"
      min="0"
      max="1"
      step="0.05"
      @change="updateFarmViz()"
    ></b-form-input>
    <!-- Block Color Setting — always rendered so the user has an escape
         hatch to "Solid" even after dropping every block-field header.
         Previously this was gated on blockFields.length > 0, which left
         orphaned colorHeader pointers (the map kept trying to color by a
         header that had been removed) with no UI affordance to recover. -->
    <p class="mb-0 mt-4"><strong>Block Color Setting</strong></p>
    <div>
      <b-btn
        @click="updateColorBy('solid')"
        variant="primary"
        size="sm"
        :class="{ active: farm.vizSettings.colorBy == 'solid' }"
        class="mr-1 mt-1"
      >
        Solid
      </b-btn>
      <b-btn
        @click="updateColorBy('valueBased')"
        variant="primary"
        size="sm"
        :class="{ active: farm.vizSettings.colorBy == 'valueBased' }"
        class="mr-1 mt-1"
      >
        Value Based
      </b-btn>
    </div>
    <p
      v-if="!hasUsableBlockField && farm.vizSettings.colorBy === 'valueBased'"
      class="text-muted small mt-2 mb-0"
    >
      No block-field headers are configured for this farm yet. Add a header (e.g. via the
      Collaborators / settings panel) before color-by-value will work, or pick "Solid" for
      a flat color.
    </p>

    <!-- <b-form-group v-if="farm.blockFields && farm.blockFields.length > 0">
      <b-form-radio
        v-on:change="updateFarmViz()"
        v-model="farm.vizSettings.colorBy"
        name="color-blocks-by"
        value="solid"
      >Solid</b-form-radio>
      <b-form-radio
        v-on:change="updateFarmViz()"
        v-model="farm.vizSettings.colorBy"
        name="color-blocks-by"
        value="valueBased"
      >Value Based</b-form-radio>
    </b-form-group> -->
    <!-- SOLID COLOR SETTINGS -->
    <p v-if="farm.vizSettings.colorBy == 'solid'" class="mb-0 mt-4"><strong>Block Color</strong></p>
    <v-swatches
      swatches="text-advanced"
      v-model="farm.vizSettings.blockColor"
      popover-x="left"
      v-on:input="updateFarmViz()"
      v-if="farm.vizSettings.colorBy == 'solid' && !farm.vizSettings.categorical"
    ></v-swatches>
    <!-- VALUE BASED COLOR SETTINGS -->
    <div v-if="farm.vizSettings.colorBy == 'valueBased'">
      <p
        v-if="farm.blockFields && farm.blockFields.length > 0"
        class="mb-0 mt-4"
      ><strong>Color Blocks by Header</strong></p>
      <div
        v-for="(header, index) in farm.blockFields"
        v-bind:key="index"
        class="btn-container"
      >
        <div
          v-if="(header.type === 'Number' || header.type === 'Select' || header.type === 'Text' || header.type === 'Date and Time') && !header.hidden"
          class="btn btn-sm btn-primary mt-1 mr-1 clickable"
          @click="setHeader(header)"
          :class="{'active':(header.machine_name === farm.vizSettings.colorHeader.machine_name)}"
        >
          {{ header.label }}
        </div><!-- /.btn -->
      </div><!-- /v-for header -->

      <!-- Number Ramp Min/Max Colors -->
      <div v-if="!showRampSettings" class="mt-4">
          <p class="lead"><i class="fas fa-spinner-third fa-spin"></i></p>
        </div>
      <div v-if="(farm.vizSettings.colorHeader.type === 'Number' || farm.vizSettings.colorHeader.type === 'Date and Time') && showRampSettings">
        <!-- RAMP SETTINGS -->
        <div>
          <p class="mb-0 mt-4"><strong>Ramp Setting</strong></p>
          <b-btn
            v-for="classification in classifications"
            v-bind:key="classification.value"
            @click="updateZoneClassification(classification.value)"
            variant="primary"
            size="sm"
            :class="{ active: farm.vizSettings.zoneClassification === classification.value }"
            class="mr-1 mt-1"
          >
            {{ classification.text }}
          </b-btn>
        </div>
        <!-- ZONE COUNT BUTTONS -->
        <div v-if="farm.vizSettings.zoneClassification !== 'linear' && farm.vizSettings.zoneClassification !== 'categorical'">
          <p class="mb-0 mt-4"><strong>Zone Count</strong></p>
          <b-btn
            v-for="zone in zones"
            v-bind:key="zone.value"
            @click="updateZoneCount(zone.value)"
            variant="primary"
            size="sm"
            :class="{ active: farm.vizSettings.zoneStops === zone.value }"
            class="mr-1 mt-1"
          >
            {{ zone.value }}
          </b-btn>
        </div>
        <!-- <div class="form-row mt-2 ml-1">
          <b-form-group id="zones-type" class="mr-1">
            <b-form-select
              id="classification"
              v-model="farm.vizSettings.zoneClassification"
              :options="classifications"
              @change="updateFarmViz()"
              required
            ></b-form-select>
          </b-form-group>
          <b-form-group
            v-if="farm.vizSettings.zoneClassification !== 'linear' && farm.vizSettings.zoneClassification !== 'categorical'"
            id="stops-group"
          >
            <b-form-select
              id="stops"
              v-model="farm.vizSettings.zoneStops"
              :options="zones"
              @change="updateFarmViz()"
              required
            ></b-form-select>
          </b-form-group>
        </div> -->

        <p
          v-if="farm.vizSettings.zoneClassification === 'linear'"
          class="mb-0 mt-4"
        ><strong>Min/Max Colors</strong></p>
        <!-- MIN COLOR SETTING -->
        <v-swatches
          swatches="text-advanced"
          v-if="farm.vizSettings.zoneClassification == 'linear'"
          v-model="farm.vizSettings.minColor"
          popover-x="left"
          v-on:input="updateFarmViz()"
          class="mr-1"
        ></v-swatches>
        <!-- MAX COLOR SETTING -->
        <v-swatches
          swatches="text-advanced"
          v-if="farm.vizSettings.zoneClassification === 'linear'"
          v-model="farm.vizSettings.maxColor"
          popover-x="left"
          v-on:input="updateFarmViz()"
        ></v-swatches>
        <div v-if="farm.vizSettings.zoneClassification !== 'categorical' && farm.vizSettings.zoneClassification !== 'linear'">
          <!-- COLOR ZONES RAMP SWATCHES -->
          <p
            v-if="farm.vizSettings.zoneClassification !== 'linear'"
            class="mb-0 mt-4"
          ><strong>Zone Colors</strong></p>

          <b-btn class="mb-2 mt-1" v-if="!showPresets" @click="showPresets = true" size="sm" variant="primary"><i class="fas fa-paint-brush"></i> Presets</b-btn>
          <b-btn class="mb-2 mt-1" v-if="showPresets" @click="showPresets = false" size="sm" variant="secondary"><i class="fas fa-times"></i> Close Presets</b-btn>

          <div v-if="showPresets" class="mb-2">
            <b-btn
              v-for="(colorPreset, i) in $store.state.colors"
              v-bind:key="i"
              @click="setColorPreset(colorPreset)"
              class="color-preset"
              size="sm"
              variant="primary"
            >
              <span
              v-for="(colorVal, i) in colorPreset"
              v-bind:key="i"
              :style="'background-color:' + colorVal"
              class="color-chip"
              ></span>
            </b-btn>
          </div>

          <div
            v-if="farm.vizSettings.zoneClassification !== 'linear' && !hideSwatches"
            class="row"
          >
            <div
              class="col-4"
              v-for="(color, i) in farm.vizSettings.colors.slice(0, farm.vizSettings.zoneStops)"
              v-bind:key="i"
            >
              <p class="mb-0">Zone {{ i + 1 }}</p>
              <v-swatches
                swatches="text-advanced"
                v-model="farm.vizSettings.colors[i]"
                popover-x="left"
                v-on:input="updateFarmViz()"
              ></v-swatches>
            </div><!-- /.col-4 -->
          </div><!-- /.row -->
        </div><!-- Number Ramp Colors -->
      </div><!-- /v-if farm.vizSettings.colorHeader.type === 'number' -->
      <div v-if="farm.vizSettings.colorHeader.type === 'Select' && farm.vizSettings.colorHeader">
        <p class="mb-0 mt-4"><strong>Category Colors</strong></p>
        <div class="row">
          <div
            class="col-4"
            v-for="(category, i) in farm.vizSettings.colorHeader.options"
            v-bind:key="i"
          >
            <p class="mb-0 mt-1">{{ category }}</p>
            <v-swatches
              swatches="text-advanced"
              v-model="farm.vizSettings.colors[i]"
              popover-x="left"
              v-on:input="updateFarmViz()"
            ></v-swatches>
          </div><!-- /.col-4 -->
        </div><!-- /.row -->
      </div><!-- Categorical Colors -->
      <div v-if="farm.vizSettings.colorHeader.type === 'Text' && farm.vizSettings.colorHeader || (farm.vizSettings.colorHeader.type === 'Number' && farm.vizSettings.zoneClassification === 'categorical')">
        <p class="mb-0 mt-4"><strong>Category Colors</strong></p>
        <div class="row">
          <div
            class="col-4"
            v-for="(category, i) in farm.vizSettings.featureCategories"
            v-bind:key="i"
          >
            <div v-if="category.name !== 'No Value'">
              <p class="mb-0 mt-1">{{ category.name }}</p>
              <v-swatches
                swatches="text-advanced"
                v-model="farm.vizSettings.colors[i]"
                popover-x="left"
                v-on:input="updateFarmViz()"
              ></v-swatches>
            </div><!-- /v-if !No Value -->
            <div v-if="category.name === 'No Value'">
              <p class="mb-0 mt-1">{{ category.name }}</p>
              <v-swatches
                swatches="text-advanced"
                value="#333"
                popover-x="left"
                v-on:input="updateFarmViz()"
              ></v-swatches>
            </div><!-- /v-if !No Value -->
          </div><!-- /.col-4 -->
        </div><!-- /.row -->
      </div><!-- Categorical Colors -->
    </div><!-- /v-if valueBased -->
    <!-- BLOCK LABEL SETTINGS -->
    <p class="mb-0 mt-4"><strong>Block Labeling</strong></p>

    <b-btn
      @click="updateLabelBy('noLabel')"
      variant="primary"
      size="sm"
      :class="{ active: farm.vizSettings.labelBy == 'noLabel' }"
      class="mr-1 mt-1"
    >
      No Label
    </b-btn>
    <b-btn
      @click="updateLabelBy('blockName')"
      variant="primary"
      size="sm"
      :class="{ active: farm.vizSettings.labelBy == 'blockName' }"
      class="mr-1 mt-1"
    >
      Block Name
    </b-btn>
    <b-btn
      @click="updateLabelBy('headerValue')"
      variant="primary"
      size="sm"
      :class="{ active: farm.vizSettings.labelBy == 'headerValue' }"
      class="mr-1 mt-1"
    >
      Header Value
    </b-btn>

    <!-- <b-form-group>
      <b-form-radio
        v-on:change="updateFarmViz()"
        v-model="farm.vizSettings.labelBy"
        name="label-blocks-by"
        value="noLabel"
      >No Label</b-form-radio>
      <b-form-radio
        v-on:change="updateFarmViz()"
        v-model="farm.vizSettings.labelBy"
        name="label-blocks-by"
        value="blockName"
      >Block Name</b-form-radio>
      <b-form-radio
        v-on:change="updateFarmViz()"
        v-model="farm.vizSettings.labelBy"
        v-if="farm.blockFields && farm.blockFields.length > 0"
        name="label-blocks-by"
        value="headerValue"
      >Header Value</b-form-radio>
    </b-form-group> -->
    <!-- VALUE BASED COLOR SETTINGS -->
    <div
      v-if="farm.vizSettings.labelBy == 'headerValue'"
      class="mb-4"
    >
      <!-- PRIMARY HEADER -->
      <p
        v-if="farm.blockFields"
        class="mb-0 mt-4"
      ><strong>Label Blocks by Header</strong></p>
      <div
        v-for="(header, index) in farm.blockFields"
        v-bind:key="index"
        class="btn-container"
      >
        <div
          v-if="(header.type === 'Number' || header.type === 'Select' || header.type === 'Text') && !header.hidden"
          class="btn btn-sm btn-primary mt-1 mr-1 clickable"
          @click="setLabelHeader(header)"
          :class="{'active':((farm.vizSettings.labelHeader) && (header.machine_name === farm.vizSettings.labelHeader.machine_name))}"
        >
          {{ header.label }}
        </div><!-- /.btn -->
      </div><!-- /v-for header -->
      <!-- LABEL BY BLOCK NAME -->
      <div
        class="btn-container"
      >
        <div
          class="btn btn-sm btn-primary mt-1 mr-1 clickable"
          @click="setLabelHeader({machine_name: 'name', type: 'Text', label: 'name'})"
          :class="{'active':(farm.vizSettings.labelHeader && farm.vizSettings.labelHeader.machine_name === 'name')}"
        >
          Name
        </div><!-- /.btn -->
      </div><!-- /. btn-container -->
      <!-- LABEL BY ACRES OR HECTARES-->
      <div
        class="btn-container"
      >
        <div
          class="btn btn-sm btn-primary mt-1 mr-1 clickable"
          @click="setLabelHeader({machine_name: 'acres', type: 'Number', label: 'acres'})"
          :class="{'active':(farm.vizSettings.labelHeader && farm.vizSettings.labelHeader.machine_name === 'acres')}"
        >
        <i class="fas fa-draw-polygon"></i> Acres
        </div><!-- /.btn -->
      </div><!-- /. btn-container -->
      <div
        class="btn-container"
      >
        <div
          class="btn btn-sm btn-primary mt-1 mr-1 clickable"
          @click="setLabelHeader({machine_name: 'hectares', type: 'Number', label: 'hectares'})"
          :class="{'active':(farm.vizSettings.labelHeader && farm.vizSettings.labelHeader.machine_name === 'hectares')}"
        >
        <i class="fas fa-draw-polygon"></i> Hectares
        </div><!-- /.btn -->
      </div><!-- /. btn-container -->

      <!-- OPTIONAL SECONDARY HEADER -->
      <p
        v-if="farm.blockFields"
        class="mb-0 mt-4"
      ><strong>Secondary Label (Optional)</strong></p>
      <div
        v-for="(header, index) in farm.blockFields"
        v-bind:key="header + index"
        class="btn-container"
      >
        <div
          v-if="(header.type === 'Number' || header.type === 'Select' || header.type === 'Text') && !header.hidden"
          class="btn btn-sm btn-primary mt-1 mr-1 clickable"
          @click="setSecondaryLabelHeader(header)"
          :class="{'active':((farm.vizSettings.secondaryLabelHeader) && (header.machine_name === farm.vizSettings.secondaryLabelHeader.machine_name))}"
        >
          {{ header.label }}
        </div><!-- /.btn -->
      </div><!-- /v-for header -->
      <!-- LABEL BY BLOCK NAME -->
      <div
        class="btn-container"
      >
        <div
          class="btn btn-sm btn-primary mt-1 mr-1 clickable"
          @click="setSecondaryLabelHeader({machine_name: 'name', type: 'Text', label: 'name'})"
          :class="{'active':(farm.vizSettings.secondaryLabelHeader && farm.vizSettings.secondaryLabelHeader.machine_name === 'name')}"
        >
          Name
        </div><!-- /.btn -->
      </div><!-- /. btn-container -->
      <!-- LABEL BY ACRES OR HECTARES-->
      <div
        class="btn-container"
      >
        <div
          class="btn btn-sm btn-primary mt-1 mr-1 clickable"
          @click="setSecondaryLabelHeader({machine_name: 'acres', type: 'Number', label: 'acres'})"
          :class="{'active':(farm.vizSettings.secondaryLabelHeader && farm.vizSettings.secondaryLabelHeader.machine_name === 'acres')}"
        >
        <i class="fas fa-draw-polygon"></i> Acres
        </div><!-- /.btn -->
      </div><!-- /. btn-container -->
      <div
        class="btn-container"
      >
        <div
          class="btn btn-sm btn-primary mt-1 mr-1 clickable"
          @click="setSecondaryLabelHeader({machine_name: 'hectares', type: 'Number', label: 'hectares'})"
          :class="{'active':(farm.vizSettings.secondaryLabelHeader && farm.vizSettings.secondaryLabelHeader.machine_name === 'hectares')}"
        >
        <i class="fas fa-draw-polygon"></i> Hectares
        </div><!-- /.btn -->
      </div><!-- /. btn-container -->
    </div><!-- v-if labelBy is headerValue -->

    <!-- Block filters — show/hide blocks by a field's value (numeric range or
         categorical, e.g. type = Variety). updateFarmViz re-applies the
         blocks-layer filter and persists vizSettings. -->
    <FarmFilters @change="updateFarmViz()" />

    <!-- LIVE ACTIVITY — realtime tracks streamed by the Collector app's
         tracker (docs/live-tracking-phase1.md). Read-only listeners; the
         toggle persists in farm.vizSettings.showLiveTracks via the normal
         updateFarmViz flow (role-gated save). -->
    <p class="mb-0 mt-4"><strong>Live Activity</strong></p>
    <p class="mb-1"><small class="text-muted">Show vehicles currently tracking in the field.</small></p>
    <b-btn
      @click="setLiveTracks(false)"
      variant="primary"
      size="sm"
      :class="{ active: !farm.vizSettings.showLiveTracks }"
      class="mr-1 mt-1"
    >
      Hide
    </b-btn>
    <b-btn
      @click="setLiveTracks(true)"
      variant="primary"
      size="sm"
      :class="{ active: farm.vizSettings.showLiveTracks == true }"
      class="mr-1 mt-1"
    >
      Show
    </b-btn>

    <!-- ====================================================================
         BASE MAP SETTINGS — pick a custom raster dataset (e.g. NAIP, a
         saved aerial scene) as the farm's base layer. Falls back to Mapbox
         satellite by default. Uses the same pill-button visual language
         (b-btn variant="primary" + active class) as the rest of this
         drawer so the section reads as a peer of Block Color Setting,
         Block Labeling, etc., not a foreign add-on.
         ==================================================================== -->
    <p class="mb-0 mt-4"><strong>Farm Base Map</strong></p>
    <b-btn
      @click="setBaseMap({ kind: 'default' })"
      variant="primary"
      size="sm"
      :class="{ active: baseMapKind === 'default' }"
      class="mr-1 mt-1"
    >
      <i class="fas fa-globe-americas"></i> Default Satellite
    </b-btn>
    <b-btn
      @click="openRasterPicker"
      variant="primary"
      size="sm"
      :class="{ active: baseMapKind === 'raster-dataset' }"
      class="mr-1 mt-1"
    >
      <i class="fas fa-image"></i> Custom Raster
    </b-btn>

    <!-- When a custom raster is active, show which one and offer a clear -->
    <p
      v-if="baseMapKind === 'raster-dataset' && activeRasterName"
      class="text-muted small mt-2 mb-0"
    >
      Currently: {{ activeRasterName }}
    </p>

    <!-- Raster picker — opens inline when the user clicks "Custom Raster" -->
    <div v-if="rasterPickerOpen" class="mt-3">
      <p class="mb-1"><strong>Choose a Raster from this Farm</strong></p>
      <div v-if="rasterDatasetsLoading" class="text-muted small">
        <i class="fas fa-spinner fa-spin"></i> Loading rasters…
      </div>
      <div v-else-if="!rasterDatasets.length" class="text-muted small">
        No raster datasets in this farm yet. Save a satellite scene as a
        raster from the Satellite drawer first.
      </div>
      <div v-else>
        <div
          v-for="ds in rasterDatasets"
          v-bind:key="ds.id"
          class="btn-container"
        >
          <div
            class="btn btn-sm btn-primary mt-1 mr-1 clickable"
            :class="{ active: farm.baseLayer && farm.baseLayer.datasetId === ds.id }"
            @click="setBaseMap({ kind: 'raster-dataset', datasetId: ds.id })"
          >
            <i class="fas fa-image"></i> {{ ds.name }}
            <span v-if="ds.metadata && ds.metadata.sceneDate" class="raster-date">
              · {{ formatSceneDate(ds.metadata.sceneDate) }}
            </span>
          </div>
        </div>
      </div>
      <b-btn
        v-if="baseMapKind === 'raster-dataset'"
        @click="setBaseMap({ kind: 'default' })"
        variant="danger"
        size="sm"
        class="mt-2"
      >
        <i class="fas fa-times"></i> Clear Custom Base Map
      </b-btn>
    </div>
  </div><!-- /#farmVizSettings -->
</template>

<script>
import VSwatches from 'vue-swatches'
import FarmFilters from './FarmFilters'
const fb = require('../../firebaseConfig.js')

export default {
  data () {
    return {
      // Base-map picker state. The picker is collapsed by default until the
      // user clicks "Custom raster…", at which point we lazy-load the farm's
      // raster datasets from Firestore.
      rasterPickerOpen: false,
      rasterDatasets: [],
      rasterDatasetsLoading: false,
      showRampSettings: true,
      showPresets: false,
      hideSwatches: false,
      classifications: [
        {
          text: 'Linear',
          value: 'linear'
        },
        {
          text: 'Equal interval',
          value: 'equal-interval'
        },
        {
          text: 'Jenks',
          value: 'jenks'
        },
        {
          text: 'Categorical',
          value: 'categorical'
        }
      ],
      defaultViz: {
        blockColor: '#6e59c7',
        colorHeader: false,
        colorBy: 'solid',
        colorOpacity: 0.8,
        colors: [
          '#e06666',
          '#f6b26b',
          '#ffd966',
          '#94c47d',
          '#70a8dc',
          '#8e7cc3',
          '#cc4125',
          '#c27ba0',
          '#77CCCC',
          '#777711',
          '#AA7744',
          '#AAAA44',
          '#774411',
          '#4477AA',
          '#AA4455',
          '#771155',
          '#DDDD77',
          '#117744',
          '#DDAA77',
          '#CC99BB',
          '#44AAAA',
          '#117777',
          '#77AADD',
          '#88CCAA',
          '#DD7788',
          '#114477',
          '#AA4488',
          '#771122',
          '#44AA77',
          '#480000',
          '#7e2b2f',
          '#b5572a',
          '#ee982a',
          '#fecd75',
          '#f4f4c1',
          '#948dbe',
          '#464fae',
          '#383890',
          '#28256f',
          '#000016',
          '#953c22',
          '#b65643',
          '#d67431',
          '#eea82d',
          '#fbe735',
          '#7ce92c',
          '#23ce34',
          '#27a57e',
          '#1f738c',
          '#1a3462',
          '#000018',
          '#00000b',
          '#421559',
          '#633661',
          '#866064',
          '#a5916b',
          '#cacb6d',
          '#93a54e',
          '#63863a',
          '#396724',
          '#1d4a12',
          '#000000'
        ],
        featureCategories: [],
        labelBy: 'noLabel',
        labelHeader: false,
        minColor: '#ffc107',
        maxColor: '#056839',
        secondaryLabelHeader: false,
        zoneClassification: 'linear',
        zoneStops: 5
      },
      zones: [
        {
          text: '3 Zones',
          value: 3
        },
        {
          text: '4 Zones',
          value: 4
        },
        {
          text: '5 Zones',
          value: 5
        },
        {
          text: '6 Zones',
          value: 6
        },
        {
          text: '7 Zones',
          value: 7
        },
        {
          text: '8 Zones',
          value: 8
        },
        {
          text: '9 Zones',
          value: 9
        },
        {
          text: '10 Zones',
          value: 10
        }
      ]
    }
  },
  computed: {
    farm: {
      get () {
        return this.$store.state.farm
      },
      set (value) {
        this.$store.commit('setStateProperty', { property: 'farm', value: value })
      }
    },
    // True when this farm has at least one block-field header that's
    // eligible to color blocks by (typed, not hidden). Used to gate the
    // "Value Based" button and to detect an orphaned colorHeader pointer.
    hasUsableBlockField () {
      const fields = (this.farm && this.farm.blockFields) || []
      const colorableTypes = new Set(['Number', 'Select', 'Text', 'Date and Time'])
      return fields.some(h => h && !h.hidden && colorableTypes.has(h.type))
    },
    // Resolved kind of the active base-map setting — drives which option is
    // highlighted in the picker. Treat "no setting" as the default.
    baseMapKind () {
      const cfg = this.farm && this.farm.baseLayer
      if (cfg && cfg.kind === 'raster-dataset' && cfg.datasetId) return 'raster-dataset'
      return 'default'
    },
    activeRasterName () {
      const cfg = this.farm && this.farm.baseLayer
      if (!cfg || cfg.kind !== 'raster-dataset') return null
      const match = this.rasterDatasets.find(d => d.id === cfg.datasetId)
      return match ? match.name : null
    },
    mapObject: {
      get () {
        return this.$store.state.mapObject
      },
      set (value) {
        this.$store.commit('setMapObject', value)
      }
    }
  },
  created () {
    // Check that default viz properties all exist
    let saveFarmViz = false
    // Same reactivity rule as DatasetVizSettings: never add vizSettings (or
    // keys on it) to the observed farm object by plain assignment — Vue 2
    // wouldn't track them and the panel would stop re-rendering. $set makes
    // the new/merged object reactive. (Also fixes the old
    // `vizSettings[key !== false]` typo, which indexed by a boolean.)
    {
      const existing = this.farm.vizSettings || {}
      const merged = { ...existing }
      if (!this.farm.vizSettings) saveFarmViz = true
      Object.keys(this.defaultViz).forEach(key => {
        if (merged[key] === 'undefined' || (!merged[key] && merged[key] !== false)) {
          merged[key] = this.defaultViz[key]
          saveFarmViz = true
        }
      })
      this.$set(this.farm, 'vizSettings', merged)
    }
    // Orphan detection — if `colorHeader` references a header that no
    // longer exists in `blockFields` (e.g. because it was just dropped),
    // fall back to Solid coloring. Without this, the map keeps trying to
    // render value-based color over a missing header and either explodes
    // or paints every block the min-color extreme. Runs after the default
    // settings hydration above so we don't fight the saveFarmViz block.
    const cb = this.farm.vizSettings.colorBy
    const ch = this.farm.vizSettings.colorHeader
    const fields = (this.farm.blockFields || [])
    const headerStillExists = ch && ch.machine_name &&
      fields.some(h => h && h.machine_name === ch.machine_name && !h.hidden)
    if (cb === 'valueBased' && !headerStillExists) {
      this.farm.vizSettings.colorBy = 'solid'
      saveFarmViz = true
    }
    if (saveFarmViz) {
      fb.farmsCollection.doc(this.$store.state.farm.id).update({
        vizSettings: this.farm.vizSettings
      })
    }
    saveFarmViz = false
    if (!this.mapObject.getLayer('blocksLayer')) {
      this.$router.push('/f/' + this.farm.id)
      this.$store.dispatch('initFarmMap')
    }
    setTimeout(() => {
      this.$store.dispatch('initFarmViz')
      this.$store.commit('resetKey', 'blocks')
    }, 1000)
  },
  components: {
    VSwatches,
    FarmFilters
  },
  methods: {
    updateZoneClassification (classification) {
      this.farm.vizSettings.zoneClassification = classification
      // this.farm.vizSettings.recalculateJenks = true
      setTimeout(() => {
        this.updateFarmViz()
      }, 100)
    },
    updateZoneCount (count) {
      this.farm.vizSettings.zoneStops = count
      // this.farm.vizSettings.recalculateJenks = true
      setTimeout(() => {
        this.updateFarmViz()
      }, 100)
    },
    updateLabelBy (value) {
      this.farm.vizSettings.labelBy = value
      this.updateFarmViz()
    },
    updateColorBy (value) {
      this.farm.vizSettings.colorBy = value
      this.updateFarmViz()
    },
    changeJenksColors () {
      this.farm.vizSettings.recalculateJenks = false
      setTimeout(() => {
        this.updateFarmViz()
      }, 100)
    },
    setColorPreset (colorPreset) {
      this.hideSwatches = true
      for (let i = 0; i < this.farm.vizSettings.zoneStops; i++) {
        const iterator = colorPreset.length / this.farm.vizSettings.zoneStops
        const colorPresetIndex = Math.round(iterator * i)
        this.farm.vizSettings.colors[i] = colorPreset[colorPresetIndex]
      }
      this.hideSwatches = false
      this.changeJenksColors()
    },
    setHeader (header) {
      this.farm.vizSettings.colorHeader = header
      this.updateFarmViz()
    },
    setLabelHeader (header) {
      console.log(header)
      this.farm.vizSettings.labelHeader = header
      this.updateFarmViz()
    },
    setSecondaryLabelHeader (header) {
      console.log(header)
      if (this.farm.vizSettings.secondaryLabelHeader && this.farm.vizSettings.secondaryLabelHeader.label === header.label) {
        this.farm.vizSettings.secondaryLabelHeader = false
      } else {
        this.farm.vizSettings.secondaryLabelHeader = header
      }
      this.updateFarmViz()
    },
    setLiveTracks (on) {
      this.$set(this.farm.vizSettings, 'showLiveTracks', on)
      if (on) {
        this.$store.dispatch('subscribeLiveTracks')
      } else {
        this.$store.dispatch('unsubscribeLiveTracks')
      }
      this.updateFarmViz()
    },
    updateFarmViz () {
      setTimeout(() => {
        this.$store.dispatch('initFarmViz')
        // Save data to cloud
        if (this.$store.state.role !== 'Administrator' && this.$store.state.role !== 'Editor') {
          console.log('Viz not saved')
        } else {
          console.log('Saving viz')
          fb.farmsCollection.doc(this.$store.state.farm.id).update({
            vizSettings: this.farm.vizSettings,
            updatedBy: this.$store.state.currentUser.uid,
            updatedOn: new Date()
          })
        }
      }, 100)
    },
    /**
     * Lazy-load the farm's raster datasets the first time the user opens
     * the picker. We pull anything where `file.raster === true` (legacy
     * raster path) OR `file.rasterDownloadPath` is set (new vector recipe
     * with a raster sidecar). The COG-resolution logic lives server-side
     * in rasterTileSource.js — here we just need an eligibility check.
     */
    async loadRasterDatasets () {
      if (this.rasterDatasetsLoading) return
      this.rasterDatasetsLoading = true
      try {
        const snap = await fb.datasetsCollection
          .where('farmId', '==', this.farm.id)
          .get()
        const out = []
        snap.forEach(doc => {
          const d = doc.data()
          d.id = doc.id
          // Trash filter — datasets in the trash folder are hidden from the
          // picker. Trashing in this app moves the dataset into a folder
          // with id 'trash' rather than setting a flag, so check folderId.
          // Also defensively skip the legacy `trashed` flag used by some
          // dynamic-collector docs.
          if (d.folderId === 'trash' || d.trashed === true) return
          const f = d.file || {}
          const eligible = (f.raster === true && f.path) || !!f.rasterDownloadPath || !!f.cogPath
          if (eligible) out.push(d)
        })
        // Newest first — most farmers will want the most recent imagery.
        out.sort((a, b) => {
          const at = a.updatedOn && a.updatedOn.toDate ? a.updatedOn.toDate() : new Date(a.updatedOn || 0)
          const bt = b.updatedOn && b.updatedOn.toDate ? b.updatedOn.toDate() : new Date(b.updatedOn || 0)
          return bt - at
        })
        this.rasterDatasets = out
      } catch (err) {
        console.error('Failed to load raster datasets:', err)
        this.rasterDatasets = []
      } finally {
        this.rasterDatasetsLoading = false
      }
    },
    openRasterPicker () {
      this.rasterPickerOpen = true
      if (!this.rasterDatasets.length) this.loadRasterDatasets()
    },
    /**
     * Persist the chosen base map and re-render so the change is visible
     * immediately. Two shapes:
     *   { kind: 'default' }                              → fall back to Mapbox
     *   { kind: 'raster-dataset', datasetId: '...' }     → custom COG via TiTiler
     */
    async setBaseMap (cfg) {
      if (this.$store.state.role !== 'Administrator' && this.$store.state.role !== 'Editor') {
        console.log('Base map change not saved (read-only role)')
        return
      }
      // Update local state first for snappy UI feedback.
      const next = cfg.kind === 'raster-dataset'
        ? { kind: 'raster-dataset', datasetId: cfg.datasetId }
        : null  // 'default' → just clear the field
      this.$store.commit('setStateProperty', {
        property: 'farm',
        value: { ...this.farm, baseLayer: next }
      })
      // Bust the cached signed URL so the map refetches with the new dataset.
      this.$store.state.customBaseLayerCache = null
      // Re-add custom base layer (or remove it on default) — no full re-render
      // needed; the action handles both directions.
      this.$store.dispatch('addCustomBaseLayer')
      // Persist to Firestore. Use FieldValue.delete() to truly clear the
      // setting when going back to default.
      try {
        const firebase = require('firebase/compat/app')
        const update = {
          updatedBy: this.$store.state.currentUser.uid,
          updatedOn: new Date(),
          baseLayer: next || firebase.firestore.FieldValue.delete()
        }
        await fb.farmsCollection.doc(this.farm.id).update(update)
      } catch (err) {
        console.error('Failed to save base map setting:', err)
      }
    },
    formatSceneDate (s) {
      try {
        const d = typeof s === 'string' ? new Date(s) : s
        if (Number.isNaN(d.getTime())) return s
        return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
      } catch (e) {
        return s
      }
    }
  }
}
</script>

<style scoped>
.mobile-buffer {
  height: 150px;
}

.color-preset {
  padding-top: 8px;
  margin-right: 4px;
}

.color-chip {
  height: 15px;
  width: 10px;
  display: inline-block;
}

/* Base-map raster pills — tiny tweak only. Inherits the project's
   .btn-primary / .active styling from _buttons.scss for the pill itself.
   Just need the trailing "· date" to render slightly muted so the dataset
   name stays the visual anchor. */
.raster-date {
  opacity: 0.75;
  font-weight: normal;
}
</style>
